<template>
  <div style="position: relative">
    <v-card flat>
      <div id="controlsContainer">
        <div
          class="card-title table-controls px-3 d-flex align-center justify-space-between"
        >
          <div>
            <!-- CRUD buttons -->
            <!-- dialog for creating item -->
            <crud-button
              v-if="$checkPermission('USERS')"
              x-large
              color="light-blue lighten-2"
              @clicked="create()"
              icon="fa-plus"
              :tooltip="$t('global.crud.button.add')"
            />
          </div>
          <!-- center side -->
          <div>
            <!-- Search by fields -->
            <!-- Search in table -->
            <v-container id="filterContainer">
              <v-row>
                <v-col lg="3" sm="6" cols="12">
                  <v-text-field
                    v-model="filter.allFields"
                    :label="$t('global.datatable.search')"
                    append-icon="fas fa-search"
                    single-line
                  ></v-text-field>
                </v-col>
                <v-col lg="2" sm="6" cols="12">
                  <v-select
                    v-model="filter.deletedItems"
                    :items="deletedItemsValues"
                    :label="$t('global.status')"
                  ></v-select>
                </v-col>
                <v-col lg="2" sm="6" cols="12">
                  <v-select
                    v-model="filter.admin"
                    :items="adminValues"
                    :label="$t('users.admin')"
                  ></v-select>
                </v-col>
                <v-col lg="3" sm="6" cols="12">
                  <v-autocomplete
                    v-model="filter.agencyCode"
                    :items="agenciesItemsFilter"
                    :loading="agenciesFilter.isLoading"
                    :search-input.sync="agenciesFilter.search"
                    hide-no-data
                    :no-data-text="$t('global.autocomplete.noDataFound')"
                    item-text="description"
                    item-value="code"
                    :label="$t('users.agency')"
                    :placeholder="$t('global.autocomplete.starTypingToSearch')"
                    @focus="agenciesFilter.isEdit = true"
                    @change="agenciesFilter.isEdit = false"
                    clearable
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-container>

            <!-- clear filters -->
          </div>
          <!-- right side -->
          <div>
            <!-- Refresh table -->
            <crud-button
              large
              color="blue"
              @clicked="refreshItemsView()"
              icon="fa-sync-alt"
              :tooltip="$t('global.crud.buttons.refreshTable')"
            />
            <ExportExcel url="/users" :filter="this.filter" />
            <ImportExcel
              url="/users"
              @done="search"
              v-if="$checkPermission('USERS')"
            />
          </div>
        </div>
      </div>
      <div id="dataContainer">
        <!-- Table -->
        <v-data-table
          :headers="headers"
          :items="items"
          :options.sync="options"
          :page.sync="pagination.page"
          :server-items-length="pagination.totalItems"
          :no-results-text="$t('global.datatable.noMatchingResults')"
          :no-data-text="$t('global.datatable.noDataAvailable')"
          :footer-props="{
            'items-per-page-options': rowsPerPageItems,
            'show-first-last-page': true,
            'show-current-page': true
          }"
          :loading="loading"
          light
          multi-sort
          dense
        >
          <template v-slot:body="{ items }">
            <tbody>
              <tr v-for="item in items" :key="item.code">
                <td>
                  <!-- Edit record -->
                  <crud-button
                    v-if="$checkPermission('USERS')"
                    x-small
                    color="orange"
                    icon="fa-pen"
                    :tooltip="$t('global.crud.buttons.edit')"
                    @clicked="edit(item.code)"
                  ></crud-button>

                  <!-- Delete record -->
                  <crud-button
                    v-if="
                      (item.deletionDate === undefined ||
                        item.deletionDate === null) &&
                        $checkPermission('USERS')
                    "
                    x-small
                    color="red"
                    icon="fa-trash"
                    :tooltip="$t('global.crud.buttons.delete')"
                    @clicked="suspend(item.code)"
                  ></crud-button>

                  <!-- Active record -->
                  <crud-button
                    v-else-if="$checkPermission('USERS')"
                    x-small
                    color="green"
                    icon="fa-redo"
                    :tooltip="$t('global.crud.buttons.restore')"
                    @clicked="restore(item.code)"
                  ></crud-button>

                  <!-- Reset password -->
                  <crud-button
                    v-if="$checkPermission('USERS')"
                    x-small
                    color="brown"
                    icon="fa-key"
                    :tooltip="$t('global.crud.buttons.password')"
                    @clicked="changePasswordDlg(item.code)"
                  ></crud-button>
                </td>
                <td>{{ item.code }}</td>
                <td>{{ item.name }} {{ item.surname }}</td>
                <td>{{ item.profile.description }}</td>
                <td>{{ item.agencyCode }}</td>
                <td><BooleanToIcon :value="item.admin" /></td>
              </tr>
            </tbody>
          </template>
        </v-data-table>
      </div>
    </v-card>
    <v-dialog v-model="dialogPassword" max-width="500px">
      <v-card>
        <v-card-title color="blue lighten-4">
          <span color="blue lighten-4" class="text-h5">
            {{ $t("global.profile.change.password") }}
          </span>
        </v-card-title>
        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="password"
                  type="password"
                  :label="$t('global.profile.change.password.new')"
                  required
                ></v-text-field>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="passwordConfirm"
                  type="password"
                  :label="$t('global.profile.change.password.confirm')"
                  required
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="changePassword()">
            {{ $t("global.confirm") }}
          </v-btn>
          <v-btn color="red darken-1" text @click="closePsw()">
            {{ $t("global.cancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="dialog" max-width="600" persistent no-click-animation>
      <validation-observer ref="obs" v-slot="{ passes }">
        <v-card>
          <v-toolbar dark color="primary" dense flat>
            <v-toolbar-title class="white--text">
              {{ formTitle }}
            </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn icon @click.native="close()">
              <v-icon>fas fa-times</v-icon>
            </v-btn>
          </v-toolbar>

          <v-card-text class="details-list">
            <v-form>
              <!-- Codigo -->
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('users.code')"
                    rules="required|max:200"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('users.code')"
                      v-model="item.code"
                      :rules="[rules.email]"
                      required
                      :error-messages="errors"
                      :disabled="editMode"
                      counter="200"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <!-- Nombre -->
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('users.name')"
                    rules="required|max:30"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('users.name')"
                      v-model="item.name"
                      required
                      :error-messages="errors"
                      counter="30"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <!-- Apellido -->
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('users.surname')"
                    rules="max:30"
                    v-slot="{ errors }"
                  >
                    <v-text-field
                      :label="$t('users.surname')"
                      v-model="item.surname"
                      :error-messages="errors"
                      counter="30"
                    ></v-text-field>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <!-- Profile -->
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('users.datatable.profile')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      v-model="item.profileCode"
                      :items="profilesItems"
                      :loading="profiles.isLoading"
                      :search-input.sync="profiles.search"
                      hide-no-data
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('users.profile')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      :error-messages="errors"
                      @focus="profiles.isEdit = true"
                      @change="profiles.isEdit = false"
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <!-- Agency -->
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('users.datatable.agency')"
                    rules="required"
                    v-slot="{ errors }"
                  >
                    <v-autocomplete
                      v-model="item.agencyCode"
                      :items="agenciesItems"
                      :loading="agencies.isLoading"
                      :search-input.sync="agencies.search"
                      hide-no-data
                      :no-data-text="$t('global.autocomplete.noDataFound')"
                      item-text="description"
                      item-value="code"
                      :label="$t('users.agency')"
                      :placeholder="
                        $t('global.autocomplete.starTypingToSearch')
                      "
                      :error-messages="errors"
                      @focus="agencies.isEdit = true"
                      @change="agencies.isEdit = false"
                      clearable
                    ></v-autocomplete>
                  </validation-provider>
                </v-flex>
              </v-layout>
              <!-- Admin -->
              <v-layout row wrap>
                <v-flex class="sm12">
                  <validation-provider
                    :name="$t('users.admin')"
                    rules="required|max:60"
                    v-slot="{ errors }"
                  >
                    <v-checkbox
                      :label="$t('users.admin')"
                      v-model="item.admin"
                      required
                      :error-messages="errors"
                    ></v-checkbox>
                  </validation-provider>
                </v-flex>
              </v-layout>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="black" text @click.native="close()">
              {{ $t("global.buttons.close") }}
            </v-btn>
            <v-btn v-if="editMode" color="orange" text @click="passes(update)">
              {{ $t("global.buttons.update") }}
            </v-btn>
            <v-btn v-else color="green" text @click="passes(save)">
              {{ $t("global.buttons.create") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </validation-observer>
    </v-dialog>
  </div>
</template>

<script>
import CrudButton from "@/components/Button.vue";
import localhttp from "../js/https-commons";
import ExportExcel from "@/components/ExportExcel.vue";
import ImportExcel from "@/components/ImportExcel.vue";
import BooleanToIcon from "@/components/BooleanToIcon.vue";

const CONTEXT = "/users";
const ENTITY_NAME = "users.";
const CONTEXT_PROFILES = "/profiles";
const CONTEXT_AGENCIES = "/agencies";

export default {
  components: {
    CrudButton,
    BooleanToIcon,
    ExportExcel,
    ImportExcel
  },
  name: "Users",
  data: () => ({
    loading: false,
    searchTableWaiting: false,
    editMode: false,
    dialog: false,
    dialogPassword: false,
    password: null,
    passwordConfirm: null,
    item: {},
    items: [],
    filter: {
      allFields: "",
      profileCode: "",
      agencyCode: "",
      deletedItems: "ACTIVE",
      admin: ""
    },
    rules: {
      email: value => {
        const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return pattern.test(value) || "Formato email incorrecto";
      }
    },
    options: {},
    pagination: {
      totalItems: 0,
      page: 1
    },
    rowsPerPageItems: [10, 20, 30, 40],
    profiles: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    profilesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agencies: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    agenciesFilter: {
      isLoading: false,
      entries: [],
      search: null,
      value: "",
      isEdit: false
    },
    usersDTO: {
      code: "",
      password: ""
    },
    strongPassword: new RegExp(
      "(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9])(?=(.*[!@#$%^&*()-__+.]))(?=.{8,})"
    )
  }),
  computed: {
    deletedItemsValues: function() {
      return [
        { text: this.$t("global.status.all"), value: "ALL" },
        { text: this.$t("global.status.active"), value: "ACTIVE" },
        { text: this.$t("global.status.deleted"), value: "DELETED" }
      ];
    },
    adminValues: function() {
      return [
        { text: this.$t("global.all"), value: "" },
        { text: this.$t("global.yes"), value: "S" },
        { text: this.$t("global.no"), value: "N" }
      ];
    },
    formTitle() {
      return this.editMode
        ? this.$t("users.title.edit")
        : this.$t("users.title.new");
    },
    headers() {
      return [
        {
          text: this.$t("global.datatable.header.actions"),
          value: "actions",
          sortable: false
        },
        {
          text: this.$t("users.datatable.code"),
          value: ENTITY_NAME + "code"
        },
        {
          text: this.$t("users.datatable.name"),
          value: ENTITY_NAME + "description"
        },
        {
          text: this.$t("users.datatable.profile"),
          value: ENTITY_NAME + "profile"
        },
        {
          text: this.$t("users.datatable.agency"),
          value: ENTITY_NAME + "agency"
        },
        {
          text: this.$t("users.datatable.admin"),
          value: ENTITY_NAME + "admin"
        }
      ];
    },
    profilesItems() {
      return this.profiles.entries.map(entry => {
        const description = entry.code + " - " + entry.description;
        return Object.assign({}, entry, { description });
      });
    },
    profilesFilterItems() {
      return this.profilesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesItems() {
      return this.agencies.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    },
    agenciesItemsFilter() {
      return this.agenciesFilter.entries.map(entry => {
        const description = entry.code + " - " + entry.name;
        return Object.assign({}, entry, { description });
      });
    }
  },
  watch: {
    options: {
      handler() {
        this.search();
      },
      deep: true
    },
    "pagination.totalItems": {
      handler() {
        this.pagination.page = 1;
      },
      deep: true
    },
    "filter.allFields": function() {
      if (!this.searchTableWaiting) {
        setTimeout(() => {
          this.search();
          this.searchTableWaiting = false;
        }, 500);
      }
      this.searchTableWaiting = true;
    },
    "profiles.search": function(value) {
      console.debug("profiles.search");
      this.preLoadGenericSearchEntity(value, this.profiles, CONTEXT_PROFILES);
    },
    "profilesFilter.search": function(value) {
      console.debug("profilesFilter.search");
      this.preLoadGenericSearchEntity(
        value,
        this.profilesFilter,
        CONTEXT_PROFILES
      );
    },
    "agencies.search": function(value) {
      console.debug("agencies.search");
      this.preLoadGenericSearchEntity(value, this.agencies, CONTEXT_AGENCIES);
    },
    "agenciesFilter.search": function(value) {
      console.debug("agenciesFilter.search");
      this.preLoadGenericSearchEntity(
        value,
        this.agenciesFilter,
        CONTEXT_AGENCIES
      );
    }
  },
  methods: {
    search() {
      console.debug("Search elements");

      // Items have already been requested
      if (this.loading) return;

      this.loading = true;

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      this.filter.sortBy = sortBy;
      this.filter.sortDesc = sortDesc;
      this.filter.page = page;
      this.filter.itemsPerPage = itemsPerPage;

      localhttp
        .post(CONTEXT + "/allPagination", this.filter)
        .then(response => {
          this.pagination.totalItems = response.data.totalElements;
          this.items = response.data.content;
          this.loading = false;
        })
        .catch(e => {
          console.log(e);
          this.loading = false;
          this.$showError();
        });
    },
    preLoadGenericSearchEntity(value, object, context, aditionalFilter) {
      if (!object.isLoadingWaiting) {
        setTimeout(() => {
          this.genericSearchEntity(object, context, aditionalFilter);
          object.isLoadingWaiting = false;
        }, 500);
      }
      object.value = value;
      object.isLoadingWaiting = true;
    },
    async genericSearchEntity(object, context, aditionalFilter) {
      console.debug("genericSearchEntity");
      if (!object.isEdit || object.value === null || object.value === "")
        return;

      if (object.isLoading) return;

      object.isLoading = true;

      let filterGeneric = {
        allFields: object.value
      };
      if (aditionalFilter !== undefined) {
        Object.keys(aditionalFilter).forEach(
          key => (filterGeneric[key] = aditionalFilter[key])
        );
      }

      // Lazily load input items
      localhttp
        .post(context + "/allFilter", filterGeneric)
        .then(response => {
          console.debug(response.data);
          object.entries = response.data;
        })
        .catch(e => {
          console.log(e);
          this.$showError();
        })
        .finally(() => (object.isLoading = false));
    },
    create() {
      console.debug("Create element");
      this.editMode = false;
      this.resetDialog();
      this.dialog = true;
    },
    async save() {
      console.debug("Save form");
      localhttp
        .post(CONTEXT + "/create", this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    edit(id) {
      console.debug("Edit element " + id);
      this.editMode = true;
      this.resetDialog();
      localhttp
        .get(CONTEXT + "/" + id)
        .then(response => {
          this.item = response.data;
          this.editPrepare(this.item);
          this.dialog = true;
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    editPrepare(item) {
      setTimeout(() => {
        this.profiles.search =
          item.profile.code + " - " + item.profile.description;
        this.agencies.search = item.agency.code + " - " + item.agency.name;
      }, 500);
    },
    async update() {
      console.debug("Update form");
      localhttp
        .put(CONTEXT + "/update/" + this.item.code, this.item)
        .then(response => {
          console.log(response.data);
          this.close();
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    async suspend(id) {
      console.debug("Suspend element " + id);
      if (
        await this.$root.$confirm(
          this.$t("global.confirm"),
          this.$t("global.action.suspend.confirm"),
          { color: "blue" }
        )
      ) {
        localhttp
          .put(CONTEXT + "/delete/" + id)
          .then(response => {
            console.log(response.data);
            this.search();
          })
          .catch(e => {
            console.log(e);
            this.$showErrorCode(e.response.status);
          });
      }
    },
    restore(id) {
      console.debug("Restore element " + id);
      localhttp
        .put(CONTEXT + "/activate/" + id)
        .then(response => {
          console.log(response.data);
          this.search();
        })
        .catch(e => {
          console.log(e);
          this.$showErrorCode(e.response.status);
        });
    },
    changePasswordDlg(id) {
      this.usersDTO.code = id;
      this.dialogPassword = true;
    },
    changePassword() {
      let pattern = this.strongPassword;

      if (this.password === this.passwordConfirm) {
        if (pattern.test(this.password)) {
          this.usersDTO.password = this.password;

          localhttp
            .put(CONTEXT + "/changePassword", this.usersDTO)
            .then(response => {
              console.log(response.data);
              this.$showMessage(this.$t("global.password.changed"));
              this.dialogPassword = false;
            })
            .catch(e => {
              console.log(e);
              this.$showErrorMessage(
                this.$t("global.password.validation.error")
              );
              this.$showErrorCode(e.response.status);
            });
        } else {
          this.$showErrorMessage(this.$t("global.password.validation.error"));
        }
      } else {
        this.$showErrorMessage(this.$t("global.password.match.error"));
      }
    },
    closePsw() {
      this.dialogPassword = false;
    },
    refreshItemsView() {
      console.debug("Refresh table");
      this.search();
    },
    close() {
      this.dialog = false;
    },
    resetDialog() {
      this.item = {};
      this.resetProfilesSearch();
      this.resetAgenciesSearch();
      requestAnimationFrame(() => {
        if (this.$refs.obs !== undefined) {
          this.$refs.obs.reset();
        }
      });
    },
    resetProfilesSearch() {
      console.debug("resetProfilesSearch");
      this.profiles.search = "";
      this.profiles.entries = [];
    },
    resetAgenciesSearch() {
      this.agencies.search = "";
      this.agencies.entries = [];
    },
    filterExport(jsonData) {
      jsonData.forEach(function(v) {
        delete v.profile;
      });
    }
  }
};
</script>
